import IEdgeData from "../../database/interfaces/IEdgeData";
import IFiltersTypeDescriptor from "../../database/interfaces/paramScript/IFiltersDescriptor";
import React from "react";
import {DatabaseContext} from "../../database/DatabaseHandler";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import {createCdnThumbnailUrl} from "../../../config/cdn";
import EdgeBorderedAvatar from "../common/EdgeBorderedAvatar";
import {SIDE_TOKENS, VISIBILITY_TOKENS, unavailableSideFlags} from "./edgeKeys";
import EdgeDecorSelectorInput from "../../common/inputs/EdgeDecorSelectorInput";
import {isEdgeAvailable} from "./edgeAvailability";

/**
 * Component displaying the inputs for an Edge product.
 */
interface IEdgeContentProps {
    edge: IEdgeData;
    format: string | null;
    filters: IFiltersTypeDescriptor | null;
    tooltip: string;
    labels: string[];
    // A megjeleníthető oldal-tokenek (top/bottom/left/right). Worktopnál a "top" kimarad.
    allowedSides: readonly string[];
    // Ha null, a láthatóság-gombcsoport NEM jelenik meg (a descriptor nem opt-in-elt rá).
    visibilityLabels: string[] | null;
    // A megjeleníthető láthatóság-tokenek — ugyanúgy, ahogy az `allowedSides` az oldal-gombokat szűri.
    allowedVisibilities: readonly string[];
    selections: string[];
    editable: boolean;
    readOnly: boolean;
    notFound: boolean;
    requiredEdgeWidth: number;
    maxEdgeWidth: number | null;
    compatibleEdgeIds: string[];
    onDecorChange: (newId: string | null, selections: string[]) => void;
    onSelectionChange: (id: string, newSelections: string[]) => void;
    onRemoveEdge: () => void;
}

const EdgeContent: React.FC<IEdgeContentProps> = ({...props}) => {
    // Use the DatabaseContext to access global data
    const dataContext = React.useContext(DatabaseContext);

    const nameFormatter = (edge: IEdgeData) => {
        // We have no format key in typedescriptors belonging to edges atm.
        return (edge.Name ?? '') +
            (edge.Thickness != null ? ` - ${edge.Thickness} ` : '') +
            (edge.Width != null ? ` / ${edge.Width} ` : '') +
            (edge.GrossUnitPrice != null ? ` (${edge.GrossUnitPrice} Ft)` : '');
    };

    const edgeFilter = (edge: IEdgeData) => {
        // Csak az elérhető élek kellenek a configuratorba: szigorúan Status = 1.
        // (uc_edgeprices.Status a shop-árazásból; 2 = csak rendelésre → nem jelenik meg.)
        if (!isEdgeAvailable(edge)) return false;
        if (edge.Width < props.requiredEdgeWidth) return false;
        if (props.maxEdgeWidth !== null && edge.Width > props.maxEdgeWidth) return false;

        if (!props.filters) return true;
        let retVal = true;
        if (retVal && props.filters.oriented !== undefined) {
            retVal = (props.filters.oriented ? edge.Oriented == 1 : edge.Oriented == 0);
        }
        if (retVal && props.filters.thickness !== undefined) {
            retVal = retVal && (edge.Thickness >= props.filters.thickness.min && edge.Thickness <= props.filters.thickness.max);
        }
        return retVal;
    };

    // A kompatibilis élek közül csak azok ID-jai, amelyek átmennek a szűrőn
    const filteredCompatibleIds = React.useMemo(() => {
        return props.compatibleEdgeIds.filter(id => {
            const edge = dataContext.edgeData?.findByID(id);
            return edge ? edgeFilter(edge) : false;
        });
    }, [props.compatibleEdgeIds, props.requiredEdgeWidth, props.maxEdgeWidth, props.filters, dataContext.edgeData]);

    const handleEdgeSelection = (id: string, token: string) => {
        const alreadySelected = props.selections.includes(token);
        let newSelections: string[];
        if (alreadySelected) {
            // Kikapcsolás: csak ezt a tokent vesszük ki.
            newSelections = props.selections.filter(s => s !== token);
        } else {
            // Bekapcsolás: a MÁSIK csoport kijelöléseit nullázzuk (nem tiltjuk a csoportot, hanem
            // a kattintás kapcsolja ki a másik csoportban lévő összeset), és csak az AZONOS
            // csoportbeli kijelöléseket tartjuk meg. ("vagy az oldalak, vagy a láthatóság")
            const tokenIsSide = (SIDE_TOKENS as readonly string[]).includes(token);
            const sameGroup = props.selections.filter(
                s => (SIDE_TOKENS as readonly string[]).includes(s) === tokenIsSide
            );
            newSelections = [...sameGroup, token];
        }

        props.onSelectionChange(id, newSelections);
    };

    const thumbnailUrl: string = createCdnThumbnailUrl(props.edge.ProductImage);
    return (
        <Stack direction="row" spacing={2} alignItems="center">

            <IconButton
                tabIndex={-1}
                disabled={!props.editable || props.readOnly}
                onClick={props.onRemoveEdge}
                size="small"
                sx={{
                    color: '#424242',
                    '&:hover': {
                        backgroundColor: '#e0e0e0',
                    },
                }}
            >
                <RemoveCircleOutlineIcon
                    fontSize="small"
                    sx={{color: props.readOnly ? 'trarnsparent' : 'inherit'}}
                />
            </IconButton>

            <EdgeBorderedAvatar
                alt={props.edge.ID}
                thumbnailUrl={thumbnailUrl}
                edges={{
                    // Csak engedélyezett + kijelölt oldal jelenik meg élzárásként a thumbnailen.
                    // Worktopnál a Top nem engedélyezett → a felső keret sem rajzolódik ki.
                    top: props.allowedSides.includes("top") && props.selections.includes("top"),
                    bottom: props.allowedSides.includes("bottom") && props.selections.includes("bottom"),
                    left: props.allowedSides.includes("left") && props.selections.includes("left"),
                    right: props.allowedSides.includes("right") && props.selections.includes("right"),
                }}
                // A nem választható oldal (munkalapnál a front: ott nincs Top) szaggatott kerettel látszik.
                unavailable={unavailableSideFlags(props.allowedSides)}
            />
            <Stack direction="column" spacing={1} alignItems="flex-start" width="100%">
                <EdgeDecorSelectorInput
                    value={props.edge.ID}
                    options={dataContext.edgeData?.children()
                        .filter(edgeFilter)
                        .sort((a, b) => {
                            const nameCompare = a.Name.localeCompare(b.Name);
                            if (nameCompare !== 0) return nameCompare;
                            return b.Thickness - a.Thickness;
                        })
                        .map(edge => ({
                            id: edge.ID,
                            name: nameFormatter(edge)
                        })) || []
                    }
                    editable={props.editable}
                    readOnly={props.readOnly}
                    notFound={props.notFound}
                    tooltip={props.tooltip}
                    compatibleIds={filteredCompatibleIds}
                    onChange={(newId) => props.onDecorChange(newId, props.selections)}
                />
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <ToggleButtonGroup
                        size="small"
                        disabled={!props.editable || props.readOnly}
                        value={props.selections}
                    >
                        {["top", "bottom", "left", "right"]
                            .map((side, index) => ({side, label: props.labels[index]}))
                            .filter(({side}) => props.allowedSides.includes(side))
                            .map(({side, label}) => (
                                <ToggleButton
                                    tabIndex={-1}
                                    key={side}
                                    value={side}
                                    selected={props.selections.findIndex(s => s === side) >= 0}
                                    onClick={() => {
                                        handleEdgeSelection(props.edge.ID, side)
                                    }}
                                >
                                    {label}
                                </ToggleButton>
                            ))}
                    </ToggleButtonGroup>
                    {props.visibilityLabels && (
                        <ToggleButtonGroup
                            size="small"
                            disabled={!props.editable || props.readOnly}
                            value={props.selections}
                        >
                            {VISIBILITY_TOKENS
                                .map((token, index) => ({token, label: props.visibilityLabels![index]}))
                                .filter(({token}) => props.allowedVisibilities.includes(token))
                                .map(({token, label}) => (
                                    <ToggleButton
                                        tabIndex={-1}
                                        key={token}
                                        value={token}
                                        selected={props.selections.findIndex(s => s === token) >= 0}
                                        onClick={() => {
                                            handleEdgeSelection(props.edge.ID, token)
                                        }}>
                                        {label}
                                    </ToggleButton>
                                ))}
                        </ToggleButtonGroup>
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
};

export default EdgeContent;
